import * as React from 'react';
import classnames from 'classnames';
import { isNil, pick } from 'lodash';
import { connect } from 'react-redux';

import { navigate } from 'libs/router';

import PageHeading from 'components/page/page-heading/PageHeading';
import { ModalOpener } from 'components/modal';
import InformationCard from 'components/information-card/InformationCard';
import DeliverableV2 from 'modules/deliverables/components/deliverable-v2/Deliverable';
import Deliverable from 'modules/deliverables/components/deliverable/Deliverable';
import DeliverableContentContext, { PAGE_TYPE } from 'modules/deliverables/components/DeliverableContentContext';
import OfferBanner from '../offer-banner/v2/OfferBanner';
import OfferCardBanner from '../offer-card-banner/OfferCardBanner';
import OfferCongratsModal from '../modals/offer-congrats-modal/OfferCongratsModal';
import RichTextPreviewModal from '../modals/RichTextPreviewModal';
import ActionButton from 'components/buttons/action-button/ActionButton';
import { withModal } from 'components/modal/ModalHOC';
import { withScrollTo } from 'components/ScrollToHOC';

import OfferShippingCard from 'modules/subcampaigns/components/offer-shipping-card/OfferShippingCard';
import CampaignDeliverablesTaskControl from 'modules/campaigns/components/deliverables/CampaignDeliverablesTaskControl';

import { offerDetailsCopied } from 'modules/subcampaigns/reducers/offerDetailsBuffer';
import { success } from 'redux-store/reducers/common-reducer';
import { downloadIndividualOffer } from 'modules/campaigns/reducers/campaignIndividualOffer';
import { fetchOfferDeliverables } from 'modules/subcampaigns/reducers/offerDeliverables';

import {
  trackEventDeliverableUploaded
} from 'modules/tracking/utils/tracking';
import { __t, applyTranslations } from 'utils/language';
import {
  DELIVERABLE_TYPE_ZALANDO_OUTFIT,
  filterDeliverables,
  getDeliverableSelectViewItemsToIgnore,
  getDeliverablesSelectViewItems,
  isZalandoOutfitOnly,
  sortNewDeliverables
} from 'utils/deliverables';
import { isInactive } from 'utils/campaign';
import {
  SUBCAMPAIGN_OFFER_SORT_VALUES,
  TASK_TAGS_COLORS,
  TASK_TAGS_COLORS_REVERSE,
  isAccepted,
  isPending
} from 'utils/subcampaign';
import { getLocationUrl, getLocationWithoutQueryElements } from 'utils/client';
import { replaceHistoryStateWithDefault } from 'utils/helper/common';
import { getHash, getUrlParam } from 'utils/url';

const ActionButtonWithPreviewModal = withModal(ActionButton, {
  modalContent: RichTextPreviewModal
});

import type {
  BrandProfile,
  Campaign,
  Deliverable as DeliverableAlias,
  DeliverableShort,
  DeliverableV2TabKey,
  MixedSelectDropdownItem,
  OrganizationSettings,
  RouterLocation,
  SubcampaignInfluencerProfile,
  SubcampaignOffer,
  SubcampaignWithInfluencerSettings
} from 'types';

applyTranslations(require('./subcampaign-offer.i18n.json'));
applyTranslations(require('modules/subcampaigns/components/copy/subcampaign-offer-copy-paste.i18n.json'));

type OwnProps = {
  subcampaign: SubcampaignWithInfluencerSettings,
  brandProfile: BrandProfile,
  influencerProfile: SubcampaignInfluencerProfile,
  isForBrand: boolean,
  offerDeliverables: Array<DeliverableAlias>,
  campaign: Campaign,
  sort?: string,
  onSortChanged?: (sort: string) => void
};

type StateProps = {
  showCongratsModal: boolean,
  location: RouterLocation,
  variablePaymentsAmount: number,
  organizationSettings: OrganizationSettings
};

type DispatchProps = {
  dispatchFetchOfferDeliverables: (a: number) => Promise<void>,
  dispatchOfferDetailsCopied: (b: SubcampaignOffer, a: Array<DeliverableAlias>) => Promise<void>,
  dispatchSuccess: (a: string) => Promise<void>,
  dispatchDownloadIndividualOffer: (b: number, a: boolean) => Promise<void>
};

type State = {
  filter?: string,
  tags: Array<MixedSelectDropdownItem>,
  expandedIdsMap: {
    [id: number]: boolean
  },
  selectedViewIdsMap: {
    [id: number]: DeliverableV2TabKey
  }
}

type Props = OwnProps & StateProps & DispatchProps;

const PageHeadingWithScrollTo = withScrollTo(PageHeading);

class SubcampaignOfferView extends React.Component<Props, State> {
  DeliverableWithScrollToV1 = withScrollTo(Deliverable, {
    className: 'subcampaign-offer__deliverable'
  });
  DeliverableWithScrollToV2 = withScrollTo(DeliverableV2, {
    suppressReplaceState: true,
    className: 'subcampaign-offer__deliverable'
  });

  provideQuoteClick?: () => void;

  constructor(props: Props) {
    super(props);
    const { isForBrand, offerDeliverables = [] } = props;

    const isV2 = offerDeliverables?.[0]?.contentApprovalV2;

    const urlView = getUrlParam('view');
    const view = (['content_approval', 'insights', 'files'].includes(urlView || '') ?
      urlView : 'information') as DeliverableV2TabKey;

    const deliverableTab = getUrlParam('tab') as DeliverableV2TabKey;
    const deliverableId = getHash('deliverable-');

    this.state = {
      filter: undefined,
      tags: this.getTags(),
      expandedIdsMap: offerDeliverables.reduce((prevValue, deliverable) => {
        // eslint-disable-next-line no-param-reassign
        prevValue[deliverable.id] = !isForBrand
        || (deliverableId === deliverable.id)
        || !(offerDeliverables[0].contentApprovalV2)
        || !isV2;

        return prevValue;
      }, {}),
      selectedViewIdsMap: this.getSelectedViewIdsMap(
        offerDeliverables,
        view,
        deliverableId,
        deliverableTab
      )
    };
  }

  getSelectedViewIdsMap(
    deliverables: Array<DeliverableAlias>,
    selectedView: DeliverableV2TabKey,
    deliverableId?: number | null,
    deliverableTab?: DeliverableV2TabKey | null
  ): { [id: number]: DeliverableV2TabKey } {
    return deliverables.reduce((prevValue, deliverable) => {
      if (deliverableTab && (deliverableId === deliverable.id)) {
        // eslint-disable-next-line no-param-reassign
        prevValue[deliverable.id] = deliverableTab;
      } else {
        // eslint-disable-next-line no-param-reassign
        prevValue[deliverable.id] = selectedView;
      }

      return prevValue;
    }, {});
  }

  getHeaderClasses(): string {
    const { isForBrand } = this.props;

    return classnames('subcampaign-offer__section-heading', {
      'subcampaign-offer__section-heading--creator': !isForBrand
    });
  }

  getVisibleOffer() {
    const { subcampaign } = this.props;
    return subcampaign.updatedOffer || subcampaign.offer;
  }

  getTags(): Array<MixedSelectDropdownItem> {
    const { offerDeliverables = [] } = this.props;
    const addedTags = [] as Array<string>;


    return offerDeliverables.reduce((tags, currentDeliverable, index) => {
      if (currentDeliverable.tag?.name && !addedTags.includes(currentDeliverable.tag.name)) {
        addedTags.push(currentDeliverable.tag.name);

        tags.push({
          id: index,
          identifier: `${index}-${currentDeliverable.tag.name}`,
          label: currentDeliverable.tag.name,
          isSelected: false,
          isDisabled: false,
          isFocused: false,
          value: currentDeliverable.tag.name,
          color: TASK_TAGS_COLORS[currentDeliverable.tag.color || '']
        });
      }

      return tags;
    }, [] as Array<MixedSelectDropdownItem>);
  }

  getFilteredDeliverables(deliverables: Array<DeliverableAlias>): Array<DeliverableAlias> {
    const { filter, tags } = this.state;

    const selectedTags = tags.reduce((tagsStr, currentTag) => {
      if (currentTag.isSelected && currentTag.color) {
        tagsStr.push(`${currentTag.value}-${TASK_TAGS_COLORS_REVERSE[currentTag.color]}`);
      }

      return tagsStr;
    }, [] as Array<string>);

    let filteredDeliverables = deliverables;

    if (selectedTags.length) {
      filteredDeliverables = filteredDeliverables.filter(deliverable =>
        deliverable.tag?.name && selectedTags.includes(`${deliverable.tag.name}-${deliverable.tag.color}`));
    }

    if (filter) {
      filteredDeliverables = filterDeliverables(filteredDeliverables, filter);
    }

    return filteredDeliverables;
  }

  getDeliverablesWithCA2 = (): Array<DeliverableShort> => {
    const { offerDeliverables } = this.props;
    const isNewDeliverable = offerDeliverables?.[0]?.contentApprovalV2;

    return isNewDeliverable ? offerDeliverables.reduce((res, d) => {
      if (d.requiresApproval) {
        res.push({
          id: d.id,
          name: d.name,
          type: d.deliverableType,
          tag: d.tag
        });
      }

      return res;
    }, [] as Array<DeliverableShort>) : [];
  };

  handleExpandAllChange = (isExpanded: boolean): void => {
    const { offerDeliverables } = this.props;

    this.setState({
      expandedIdsMap: offerDeliverables.reduce((prevValue, deliverable) => {
        // eslint-disable-next-line no-param-reassign
        prevValue[deliverable.id] = isExpanded;

        return prevValue;
      }, {})
    });
  };

  handleExpandDeliverable = (deliverableId: number): void => {
    this.setState(prevState => ({
      ...prevState,
      expandedIdsMap: {
        ...prevState.expandedIdsMap,
        [deliverableId]: !prevState.expandedIdsMap[deliverableId]
      }
    }));
  };

  handleTagFilterChange = (updatedItems): void => {
    this.setState({
      tags: updatedItems
    });
  };

  handleRemoveAllTags = () => {
    this.setState({
      tags: this.getTags()
    });
  };

  handleSortChanged = (sort: string) => {
    const { onSortChanged } = this.props;

    if (onSortChanged) {
      onSortChanged(sort);
    }
  };

  handleListFilterChanged = (filter?: string) => {
    this.setState({
      filter
    });
  };

  handleEditClick = () => {
    const { subcampaign } = this.props;

    const redirectToEdit = `/campaigns/${subcampaign.campaign.id}/subcampaigns/${subcampaign.id}/edit?existing`;
    navigate.to(redirectToEdit);
  };

  handleOfferCongratsModalClose = (): void => {
    const { location } = this.props;

    const newLocation = getLocationWithoutQueryElements(location, ['showOfferCongratsModal']);

    navigate.to(getLocationUrl(newLocation));
  };

  handleCopyOfferDetails = () => {
    const { subcampaign, offerDeliverables, dispatchOfferDetailsCopied, dispatchSuccess } = this.props;

    const latestOffer = subcampaign.updatedOffer || subcampaign.offer;

    dispatchOfferDetailsCopied(latestOffer, offerDeliverables);
    dispatchSuccess(__t('subcampaign_offer_copy_paste.offer_details_copied_merged_page'));
  };

  handleContentMoved = async () => {
    const { subcampaign, dispatchFetchOfferDeliverables } = this.props;
    const { offer } = subcampaign;

    await dispatchFetchOfferDeliverables(offer.id);
  };

  handleDownloadOffer = () => {
    const { subcampaign, isForBrand, dispatchDownloadIndividualOffer } = this.props;

    dispatchDownloadIndividualOffer(subcampaign.id, isForBrand);
  };

  handleProvideQuote = () => {
    if (this.provideQuoteClick) {
      this.provideQuoteClick();
    }
  };

  handleDeliverableChangeView = (deliverableId, selectedView: DeliverableV2TabKey) => {
    this.setState(prevState => ({
      ...prevState,
      selectedViewIdsMap: {
        ...prevState.selectedViewIdsMap,
        [deliverableId]: selectedView
      }
    }));
  };

  handleChangeView = (selectedView: DeliverableV2TabKey) => {
    const { offerDeliverables } = this.props;

    replaceHistoryStateWithDefault();

    this.setState(prevState => ({
      ...prevState,
      selectedViewIdsMap: this.getSelectedViewIdsMap(offerDeliverables, selectedView)
    }));
  };

  renderPageHeading(): React.ReactNode {
    const { subcampaign } = this.props;
    const influencerProfile = subcampaign.creatorProfile;
    const headerClasses = this.getHeaderClasses();

    return (
      <PageHeading
        heading={
          __t('subcampaign_offer_module.individual_offer_subtitle.singular', {
            influencer: influencerProfile.name
          })
        }
        size="h3"
        margin="none"
        weight="bold"
        customClass={headerClasses}
      />
    );
  }

  renderPageHeadingWithScrollTo(): React.ReactNode {
    const headerClasses = this.getHeaderClasses();

    return (
      <PageHeadingWithScrollTo
        heading={__t('subcampaign_offer_module.deliverables')}
        size="h3"
        hash="deliverables"
        margin="none"
        customClass={headerClasses}
      />
    );
  }

  renderBar(): React.ReactNode {
    const {
      subcampaign,
      influencerProfile,
      brandProfile,
      isForBrand,
      campaign,
      offerDeliverables,
      variablePaymentsAmount
    } = this.props;

    const offer = this.getVisibleOffer();
    const allowEditAction = !isInactive(campaign);
    const isPendingOrAcceptedSubcampaign = isAccepted(subcampaign) || isPending(subcampaign);

    return (
      <div className="subcampaign-offer__brand-offer-banner">
        <OfferCardBanner
          isForBrand={isForBrand}
          influencerProfile={influencerProfile}
          brandProfile={brandProfile}
          subcampaign={subcampaign}
          visibleOffer={offer}
          visibleDeliverables={offerDeliverables}
          variablePaymentsAmount={variablePaymentsAmount}
          isBulkOfferActive={false}
          bulkOfferSubcampaigns={[]}
          allowEditAction={allowEditAction}
          allowBulkEditAction={allowEditAction}
          onEditClick={this.handleEditClick}
          onCopyOfferDetails={this.handleCopyOfferDetails}
          onProvideQuoteClick={this.handleProvideQuote}
          onDownloadOffer={isForBrand && isPendingOrAcceptedSubcampaign ? this.handleDownloadOffer : undefined}
        />
      </div>
    );
  }

  renderOfferBanner(): React.ReactNode {
    const { brandProfile, subcampaign, isForBrand } = this.props;

    return (
      <OfferBanner
        subcampaign={subcampaign}
        isBrand={isForBrand}
        brandProfileName={brandProfile?.name ?? ''}
        setProvideQuoteClick={(provideQuoteClick) => { this.provideQuoteClick = provideQuoteClick; }}
        onBrandEditOfferAfterReceivingQuote={this.handleEditClick}
      />
    );
  }

  renderOfferBannerSection(): React.ReactNode {
    const headerClasses = this.getHeaderClasses();

    return (
      <React.Fragment>
        <PageHeading
          heading={__t('subcampaign_offer_module.offer_title')}
          size="h3"
          margin="none"
          weight="bold"
          customClass={headerClasses}
        />
        {this.renderOfferBanner()}
      </React.Fragment>
    );
  }

  renderShipping(offer: SubcampaignOffer) {
    const { organizationSettings, subcampaign, isForBrand } = this.props;
    const influencerOrganizationSettings = isForBrand ? pick(subcampaign, ['bankDetails', 'billingAddress', 'businessDetails', 'proofDocuments']) : organizationSettings;
    const headerClasses = this.getHeaderClasses();

    return (
      <OfferShippingCard
        isForBrand={isForBrand}
        subcampaignId={subcampaign.id}
        headingClass={headerClasses}
        includesDelivery={offer.includesShipping}
        shippingAddress={subcampaign.shippingAddress}
        isOfferAccepted={offer.state === 'accepted'}
        influencerOrganizationSettings={influencerOrganizationSettings}
      />
    );
  }

  renderInformationCard(): React.ReactNode {
    const offer = this.getVisibleOffer();

    return (
      <InformationCard
        title=""
        brief={offer.legalRequirements ? __t('subcampaign_offer_module.legal_requirements_brief') : ''}
        description={offer.legalRequirements}
        previewMode={offer.legalRequirements ? 'external' : 'inline'}
        action={offer.legalRequirements
          ? (
            <ActionButtonWithPreviewModal
              modalContentProps={{
                title: __t('subcampaign_offer_module.legal_requirements'),
                text: offer.legalRequirements
              }}
            >
              {__t('subcampaign_offer_module.action_button_view')}
            </ActionButtonWithPreviewModal>
          )
          : null
        }
      />
    );
  }

  renderFilterBar(): React.ReactNode {
    const { isForBrand, offerDeliverables, sort } = this.props;
    const { filter, tags, selectedViewIdsMap, expandedIdsMap } = this.state;
    const isV2 = offerDeliverables?.[0]?.contentApprovalV2;
    const filterLinksToIgnore = getDeliverableSelectViewItemsToIgnore(offerDeliverables);
    const selectViewItems = getDeliverablesSelectViewItems(filterLinksToIgnore);
    const zoutfitsOnly = isZalandoOutfitOnly(offerDeliverables);

    return (
      <div className="subcampaign-offer__tag-filter-container subcampaign-offer__tag-filter-container--view">
        <CampaignDeliverablesTaskControl
          isForBrand={isForBrand}
          contentApprovalV2={Boolean(isV2)}
          sortOptions={SUBCAMPAIGN_OFFER_SORT_VALUES}
          sort={sort}
          filter={filter}
          tags={tags}
          selectedViewIdsMap={selectedViewIdsMap}
          expandedIdsMap={expandedIdsMap}
          selectViewItems={selectViewItems}
          zoutfitsOnly={zoutfitsOnly}
          onTagFilterChange={this.handleTagFilterChange}
          onSortChanged={this.handleSortChanged}
          onListFilterChanged={this.handleListFilterChanged}
          onChangeView={this.handleChangeView}
          onExpandAllChange={this.handleExpandAllChange}
        />
      </div>
    );
  }

  renderOffer = (content: React.ReactNode):React.ReactNode => (
    <div className="subcampaign-offer">
      {this.renderBar()}
      {content}
    </div>
  );

  renderDeliverable = (deliverable: DeliverableAlias, index: number): React.ReactNode => {
    const { campaign, subcampaign, isForBrand, offerDeliverables } = this.props;
    const { expandedIdsMap, selectedViewIdsMap } = this.state;
    const offer = this.getVisibleOffer();
    const isNewDeliverable = offerDeliverables?.[0]?.contentApprovalV2;
    const deliverablesWithCA2 = this.getDeliverablesWithCA2();
    const zalandoOutfitsdeliverablesWithCA2 = deliverablesWithCA2.filter(d => d.type === DELIVERABLE_TYPE_ZALANDO_OUTFIT);

    const DeliverableWithScrollTo = isNewDeliverable ? this.DeliverableWithScrollToV2 : this.DeliverableWithScrollToV1;

    return (
      <DeliverableContentContext.Provider
        value={PAGE_TYPE.OFFER}
        key={`subcampaign-offer-deliverable-${index}-${deliverable.id}`}
      >
        <DeliverableWithScrollTo
          key={`subcampaign-offer-deliverable-main-${index}-${deliverable.id}`}
          campaignId={campaign.id}
          subcampaign={subcampaign}
          deliverable={deliverable}
          offerId={offer.id}
          deliverablesWithCA2={
            deliverable.deliverableType === DELIVERABLE_TYPE_ZALANDO_OUTFIT ? zalandoOutfitsdeliverablesWithCA2 : deliverablesWithCA2
          }
          hash={`deliverable-${deliverable.id}`}
          isForBrand={isForBrand}
          isExpanded={expandedIdsMap[deliverable.id]}
          onTrackDeliverableUploadEvent={
            eventAction => trackEventDeliverableUploaded(eventAction, 'task cards on content page')
          }
          selectedTab={selectedViewIdsMap[deliverable.id]}
          onExpanded={() => this.handleExpandDeliverable(deliverable.id)}
          onContentMoved={this.handleContentMoved}
          onViewChange={(selectedView: DeliverableV2TabKey) => this.handleDeliverableChangeView(deliverable.id, selectedView)}
        />
      </DeliverableContentContext.Provider>
    );
  };

  render(): React.ReactNode {
    const { subcampaign, isForBrand, offerDeliverables, showCongratsModal, sort } = this.props;
    const offer = this.getVisibleOffer();
    const deliverablesList = sortNewDeliverables(offerDeliverables, sort || SUBCAMPAIGN_OFFER_SORT_VALUES[0]);
    const filteredDeliverablesList = this.getFilteredDeliverables(deliverablesList);
    const headerClasses = this.getHeaderClasses();

    return (
      <React.Fragment>
        {
          !isForBrand &&
            this.renderPageHeading()
        }
        {
          this.renderOffer(
            <React.Fragment>
              {this.renderOfferBannerSection()}
              {this.renderShipping(offer)}
              <PageHeading
                heading={__t('subcampaign_offer_module.legal_requirements')}
                size="h3"
                margin="none"
                weight="bold"
                customClass={headerClasses}
              />
              {this.renderInformationCard()}
              {
                deliverablesList.length > 0 && (
                  <React.Fragment>
                    {this.renderPageHeadingWithScrollTo()}
                    {this.renderFilterBar()}
                    {filteredDeliverablesList.map(this.renderDeliverable)}
                  </React.Fragment>
                )
              }
            </React.Fragment>
          )
        }
        <ModalOpener
          open={isForBrand && showCongratsModal}
          width="small"
          onClose={this.handleOfferCongratsModalClose}
        >
          <OfferCongratsModal
            subcampaign={subcampaign}
            onClose={this.handleOfferCongratsModalClose}
          />
        </ModalOpener>
      </React.Fragment>
    );
  }
}

const mapStateToProps = ({ router, subcampaignVariablePayments, organizationSettings }): StateProps => ({
  showCongratsModal: router.location.query && !isNil(router.location.query.showOfferCongratsModal),
  location: router.location,
  variablePaymentsAmount: subcampaignVariablePayments.variablePayments.totalAmount || 0,
  organizationSettings: organizationSettings.organization
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchOfferDeliverables: (offerId: number) => dispatch(fetchOfferDeliverables(offerId, true)),

  dispatchOfferDetailsCopied: (offer, deliverables) => dispatch(offerDetailsCopied(offer, deliverables)),
  dispatchSuccess: (message) => dispatch(success(message)),

  dispatchDownloadIndividualOffer: (subcampaignId: number, isForBrand: boolean) =>
    dispatch(downloadIndividualOffer(subcampaignId, isForBrand))
});

export default connect(mapStateToProps, mapDispatchToProps)(SubcampaignOfferView);
